import { useState, useRef, useMemo } from 'react';
import { useLazyGetPriceInformationQuery, useLazyGetPriceHistoryQuery } from '../../../api/endpoints/dealerPartsCatalog';
import { useLanguage } from '../../../contexts/LanguageContext';

export const usePriceList = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [activeTab, setActiveTab] = useState(0);
    const [filters, setFilters] = useState({
        partCode: '',
        languageId: 1,
        pageNumber: 1,
        pageSize: 10
    });
    const [selectedPartCode, setSelectedPartCode] = useState('');
    const [showFilters, setShowFilters] = useState(true);
    const [first, setFirst] = useState(0);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const [triggerPriceListSearch, { data: priceData = {}, isLoading: isLoadingPrices }] = useLazyGetPriceInformationQuery();
    const [triggerPriceHistorySearch, { data: priceHistoryData = {}, isLoading: isLoadingHistory }] = useLazyGetPriceHistoryQuery();

    const rawPriceList = priceData?.data?.items || [];
    const totalRecords = priceData?.data?.totalCount || 0;
    const priceHistoryList = priceHistoryData?.data?.items || [];
    const priceHistoryTotalRecords = priceHistoryData?.data?.totalCount || 0;

    const priceList = useMemo(() => {
        if (!sortField || !sortOrder) return rawPriceList;

        return [...rawPriceList].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'string') {
                return sortOrder === 1 ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            if (typeof aVal === 'number') {
                return sortOrder === 1 ? aVal - bVal : bVal - aVal;
            }

            return 0;
        });
    }, [rawPriceList, sortField, sortOrder]);

    const handleSearch = () => {
        const newFilters = { ...filters, pageNumber: 1 };
        setFirst(0);
        setFilters(newFilters);
        if (activeTab === 0) {
            triggerPriceListSearch(newFilters);
        } else {
            triggerPriceHistorySearch(newFilters);
        }
    };

    // Alt tab'a göre doğru endpoint'e istek at
    const searchByPartCode = (partCode, subTab = activeTab) => {
        const newFilters = { ...filters, partCode, pageNumber: 1 };
        setFirst(0);
        setFilters(newFilters);
        if (subTab === 0) {
            // Fiyat Listesi
            triggerPriceListSearch(newFilters);
        } else {
            // Fiyat Geçmişi
            setSelectedPartCode(partCode);
            triggerPriceHistorySearch(newFilters);
        }
    };

    const handleReset = () => {
        setFirst(0);
        setSortField(null);
        setSortOrder(null);
        setFilters({ partCode: '', languageId: 1, pageNumber: 1, pageSize: 10 });
        setSelectedPartCode('');
        setActiveTab(0);
    };

    const onPageChange = (event) => {
        const newPageNumber = Math.floor(event.first / event.rows) + 1;
        setFirst(event.first);
        const newFilters = {
            ...filters,
            pageSize: event.rows,
            pageNumber: newPageNumber
        };
        setFilters(newFilters);
        if (filters.partCode) {
            if (activeTab === 0) {
                triggerPriceListSearch(newFilters);
            } else {
                triggerPriceHistorySearch(newFilters);
            }
        }
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const handleHistoryClick = (partCode) => {
        setSelectedPartCode(partCode);
        setActiveTab(1);
    };

    const formatCurrency = (amount, currency = 'TRY') => {
        if (amount === null || amount === undefined || isNaN(amount)) return '-';
        return new Intl.NumberFormat(th('common.locale'), { style: 'currency', currency: currency }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString(th('common.locale'));
    };

    return {
        toast,
        activeTab,
        setActiveTab,
        filters,
        setFilters,
        selectedPartCode,
        showFilters,
        setShowFilters,
        priceList,
        totalRecords,
        priceHistoryList,
        priceHistoryTotalRecords,
        isLoadingPrices,
        isLoadingHistory,
        handleSearch,
        searchByPartCode,
        handleReset,
        handleHistoryClick,
        formatCurrency,
        formatDate,
        first,
        onPageChange,
        onSort,
        sortField,
        sortOrder,
        th
    };
};
